import {
  GetClipsSort,
  QueryGetClipsByCampaignIdArgs,
  User,
} from '../../../gql/types'
import { useQuery } from '@apollo/client'
import {
  GET_CLIPS_BY_CAMPAIGN_ID,
  GET_CREATORS_BY_CAMPAIGN_WITH_CLIPS,
  GetClipsByCampaignResponse,
  QueryGetCreatorsByCampaignWithClipsResponse,
} from '../../../gql'
import { Header } from '../components/Header'
import { useParams } from 'react-router'
import { ContentGrid } from '../../../components/ContentGrid'
import { LIMIT_DEFAULT } from '../../../utils/constants'
import { useState, useMemo } from 'react'
import Multiselect from '../../../elements/Multiselect'
import { ListOptions } from '../../../components'
import { Avatar } from '../../../elements'

interface IProps {
  user: User
}

export const Clips = ({ user }: IProps) => {
  const { campaignId } = useParams()
  const limit = LIMIT_DEFAULT
  const [selectedCreators, setSelectedCreators] = useState<any[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [currentQuery, setCurrentQuery] = useState('')

  // Fetch creators with clips for this campaign
  const { data: creatorsData } =
    useQuery<QueryGetCreatorsByCampaignWithClipsResponse>(
      GET_CREATORS_BY_CAMPAIGN_WITH_CLIPS,
      {
        variables: {
          campaignId: +(campaignId as string),
        },
      },
    )

  // Create creator options for multiselect
  const creatorOptions = useMemo(() => {
    if (!creatorsData?.getCreatorsByCampaignWithClips) return []

    return [...creatorsData.getCreatorsByCampaignWithClips]
      .sort((a, b) => a.firstName.localeCompare(b.firstName))
      .map((creator) => ({
        value: creator.id.toString(),
        label: creator.firstName,
        avatar: (
          <Avatar
            type='user'
            size='xxs'
            src={creator.avatar?.url}
            alt={creator.firstName}
          />
        ),
      }))
  }, [creatorsData])

  // Create creatorIds array for filtering
  const creatorIds = useMemo(() => {
    return selectedCreators.map((creator) => +creator.value)
  }, [selectedCreators])

  const { loading, error, data, fetchMore, refetch } = useQuery<
    GetClipsByCampaignResponse,
    QueryGetClipsByCampaignIdArgs
  >(GET_CLIPS_BY_CAMPAIGN_ID, {
    variables: {
      campaignId: +(campaignId as string),
      options: {
        query: currentQuery,
        filters: {
          creatorIds: creatorIds.length > 0 ? creatorIds : undefined,
        },
        sort: GetClipsSort.newest,
        limit,
        skip: 0,
      },
    },
  })

  const fetchData = () => {
    if (loading) {
      return
    }

    // if we have all the data, don't fetch more
    if (
      data?.getClipsByCampaignId?.items?.length >=
      data?.getClipsByCampaignId?.pagination?.numberOfHits
    ) {
      return
    }

    fetchMore({
      variables: {
        campaignId: +(campaignId as string),
        options: {
          query: currentQuery,
          filters: {
            creatorIds: creatorIds.length > 0 ? creatorIds : undefined,
          },
          sort: GetClipsSort.newest,
          limit,
          skip: data?.getClipsByCampaignId?.items?.length ?? 0,
        },
      },
    })
  }

  // Handle creator selection change
  const handleCreatorChange = (newSelection: any[] | null) => {
    setSelectedCreators(newSelection || [])
    refetch({
      campaignId: +(campaignId as string),
      options: {
        query: currentQuery,
        filters: {
          creatorIds:
            newSelection && newSelection.length > 0
              ? newSelection.map((creator) => +creator.value)
              : undefined,
        },
        sort: GetClipsSort.newest,
        limit,
        skip: 0,
      },
    })
  }

  // Handle search
  const onSearch = () => {
    setCurrentQuery(searchQuery)
    refetch({
      campaignId: +(campaignId as string),
      options: {
        query: searchQuery,
        filters: {
          creatorIds: creatorIds.length > 0 ? creatorIds : undefined,
        },
        sort: GetClipsSort.newest,
        limit,
        skip: 0,
      },
    })
  }

  if (error) {
    console.log(error)
    return <p>There has been an error. Try refreshing the page</p>
  }

  return (
    <div className='overflow-y-scroll'>
      <Header />

      <ListOptions
        query={searchQuery}
        setQuery={setSearchQuery}
        onSearch={onSearch}
        searchPlaceholder='Search by campaign or creator name...'
        rightComponent={
          <Multiselect
            options={creatorOptions}
            selected={selectedCreators}
            onChange={handleCreatorChange}
            placeholder='Select creators...'
            customText={
              selectedCreators.length > 0
                ? `${selectedCreators.length} creators selected`
                : ''
            }
            className='w-full md:w-[400px] rounded-lg shadow-none text-gray-500 text-sm py-2'
            maxMenuHeight={400}
            displayTags={true}
            maxTagDisplay={3}
          />
        }
        searchOnLeft={true}
        className='border-b-0 !bg-transparent'
      />

      <ContentGrid
        clips={data?.getClipsByCampaignId?.items || []}
        hasMore={
          data?.getClipsByCampaignId?.items?.length <
          data?.getClipsByCampaignId?.pagination?.numberOfHits
        }
        fetchData={fetchData}
        loading={loading}
        allowDownload={true}
        type='brand'
      />
    </div>
  )
}
